import { useDrillSession } from '../engine/useDrillSession';
import type { OrgConfig } from '../engine/types';
import { AttemptSummary } from './AttemptSummary';

/**
 * §19 — drills are the simplest screen in the product: stimulus, choices,
 * immediate feedback, next. Optimized for repetition speed.
 */
export function DrillRunner({
  orgId, uid, drillId, config, salesRole, onDone,
}: {
  orgId: string;
  uid: string;
  drillId: string;
  config: OrgConfig;
  salesRole: string | null;
  onDone: () => void;
}) {
  const session = useDrillSession(orgId, uid, drillId, config, salesRole);
  const {
    drill, item, keyItem, choices, index, total, phase, error,
    selected, explainTapped, result, scoreSource, tells, answer, next, tapExplain,
  } = session;

  if (phase === 'error') {
    return (
      <main className="centered">
        <div className="card">
          <h1>Something went wrong</h1>
          <p className="error">{error}</p>
          <button className="primary" onClick={onDone}>Back to your progress</button>
        </div>
      </main>
    );
  }

  if (phase === 'loading' || !drill || !item) {
    return <main className="centered"><p className="muted">Loading…</p></main>;
  }

  if (phase === 'scored' && result) {
    return (
      <AttemptSummary
        drill={drill} result={result} source={scoreSource} config={config} onDone={onDone}
      />
    );
  }

  const chosen = choices.find((c) => c.id === selected)?.text ?? null;
  const correct = keyItem ? selected === keyItem.preferredId : null;
  const acceptable = keyItem ? !!keyItem.acceptableIds?.includes(selected ?? '') : false;
  const verdict = correct ? 'correct' : acceptable ? 'acceptable' : 'incorrect';
  const showing = phase === 'feedback' && keyItem;

  return (
    <main className="drill">
      <header className="drill-head">
        <button className="ghost small" onClick={onDone}>Leave</button>
        {/* A bar as well as the numbers. Seventeen items is a long way, and
            "1 / 17" tells you where you are without telling you how far that
            is; a rep pacing themselves reads the bar and not the fraction. */}
        <div className="progress" role="progressbar"
          aria-valuenow={index + 1} aria-valuemin={1} aria-valuemax={total}
          aria-label={`Question ${index + 1} of ${total}`}>
          <div className="progress-fill" style={{ width: `${((index + 1) / total) * 100}%` }} />
        </div>
        <span className="counter">{index + 1} / {total}</span>
      </header>

      <section className="stimulus">
        {/* §12.6, §12.7 — which technique is on trial. It is the premise of the
            item rather than its answer, and a comparison between two versions
            of an unnamed move is a comparison of two sentences. */}
        {item.aboutTactic && (
          <p className="item-tactic">{item.aboutTactic.replace(/_/g, ' ')}</p>
        )}
        {item.stimulus.prompt && <p className="prompt">{item.stimulus.prompt}</p>}
        {item.stimulus.lines?.map((line, i) => (
          <p key={i} className={`line line-${line.speaker}`}>
            <span className="speaker">{line.speaker === 'rep' ? 'You' : 'Them'}</span>
            {line.text}
          </p>
        ))}
      </section>

      {!showing && (
        <>
          {/* The task, given the weight of a task. It used to be the dimmest
              text on the screen while being the thing actually being asked. */}
          {drill.instructions && index === 0 && <p className="instructions">{drill.instructions}</p>}
          <ul className="choices">
            {choices.map((choice) => (
              <li key={choice.id}>
                <button className="choice" onClick={() => answer(choice.id)}>
                  {choice.text}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      {showing && keyItem && (
        <section className={`feedback feedback-${verdict}`} aria-live="polite">
          <h2>
            {verdict === 'correct' && 'Correct'}
            {verdict === 'acceptable' && 'Defensible — but not the strongest read'}
            {verdict === 'incorrect' && 'Not this one'}
          </h2>

          {/* What they actually said, in their own words, still on the screen.
              Feedback on a choice you can no longer see is feedback on
              something you have to remember instead of something you can read,
              and the whole point of the pause is to connect the two. */}
          {chosen && (
            <p className="chose">
              You said <strong>{chosen}</strong>.
            </p>
          )}

          {verdict !== 'correct' && (
            <p className="answer">
              The answer is <strong>{labelFor(keyItem.preferredId, config)}</strong>.
            </p>
          )}

          {tells.length > 0 && (
            <div className="tells">
              {/* The same list means two different things depending on how it
                  went: confirmation of what you read, or what you missed. */}
              <h3>{verdict === 'correct' ? 'What told you' : 'What to catch next time'}</h3>
              <ul>{tells.map((tell, i) => <li key={i}>{tell}</li>)}</ul>
            </div>
          )}

          <p className="explain">{keyItem.explain.what}</p>

          {keyItem.explain.better && (
            explainTapped
              ? <p className="explain better">{keyItem.explain.better}</p>
              : <button className="ghost" onClick={tapExplain}>What should I do with this?</button>
          )}

        </section>
      )}

      {/* §19 — one screen, no scrolling during a turn. The explanation can run
          longer than a phone, so the action that moves on is pinned rather than
          waiting at the end of it: a rep should never have to scroll to
          continue, and one that does starts skimming the explanation to reach
          the button. */}
      {showing && (
        <div className="action-bar">
          <button className="primary wide" onClick={next}>
            {index >= total - 1 ? 'Finish' : 'Next'}
          </button>
        </div>
      )}
    </main>
  );
}

/** Labels resolve from the content pack, never from the engine (§1.1). */
function labelFor(id: string, config: OrgConfig): string {
  const persona = config.personas.find((p) => p.id === id);
  if (persona) return persona.name;
  return id.replace(/_/g, ' ');
}
